import IconPreferencesActive from 'common/assets/image/ichi/icons/dark/icon_active_settings.svg';
import IconClose from 'common/assets/image/ichi/icons/dark/icon_close.svg';
import IconFuse from 'common/assets/image/ichi/icons/dark/icon_fuse.svg';
import IconIchiOwned from 'common/assets/image/ichi/icons/dark/icon_ichi_blue.svg';
import IconIchiWhite from 'common/assets/image/ichi/icons/dark/icon_ichi_white.svg';
import IconPreferencesInactive from 'common/assets/image/ichi/icons/dark/icon_inactive_settings.svg';
import IconPreferencesInactiveMobile from 'common/assets/image/ichi/icons/dark/icon_inactive_settings_mobile.svg';
import IconHelp from 'common/assets/image/ichi/icons/dark/icon_info.svg';
import IconLp from 'common/assets/image/ichi/icons/dark/icon_lp.svg';
import IconRenFil from 'common/assets/image/ichi/icons/dark/icon_renFIL.svg';
import IconEthWhite from 'common/assets/image/ichi/icons/dark/icon_white_eth.svg';
import IconMaticWhite from 'common/assets/image/ichi/icons/dark/icon_white_matic.svg';
import IconXIchiOwned from 'common/assets/image/ichi/icons/dark/icon_xichi_green.svg';
import IconEth from 'common/assets/image/ichi/icons/light/eth_black.svg';
import IconIchiApy from 'common/assets/image/ichi/icons/light/icon_ichi_percentage.svg';
import IconXIchiIchiRatio from 'common/assets/image/ichi/icons/light/icon_ichi_xichi.svg';
import IconNewWindow from 'common/assets/image/ichi/icons/light/icon_new_window.svg';
import IconNewWindowWhite from 'common/assets/image/ichi/icons/light/icon_new_window_white.svg';
import IconWBTC from 'common/assets/image/ichi/icons/light/icon_wbtc.svg';
import IconSidebar from 'common/assets/image/ichi/logo_horizontal_dark/ichi_logo_horizontal_dark.svg';
import IconTheme from 'common/assets/image/ichi/theme/dark_mode.svg';
import IconThemeMobile from 'common/assets/image/ichi/theme/dark_mode_mobile.svg';
import BadgePolygon from 'common/assets/image/polygon/dark_polygon_badge.svg';
import { ColorsKey, IchiTheme, Theme } from 'common/models/theme';
import { css } from 'styled-components';
import darkColors from './darkColors';

const MEDIA_WIDTHS = {
  upToExtraSmall: 500,
  upToSmall: 720,
  upToMedium: 960,
  upToLarge: 1280,
};

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce((accumulator, size) => {
  (accumulator as any)[size] = (a: any, b: any, c: any) => css`
    @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
      ${css(a, b, c)}
    }
  `;
  return accumulator;
}, {}) as any;

export const darkTheme: IchiTheme = {
  name: Theme.DARK,
  mediaWidth: mediaWidthTemplates,
  icons: {
    sidebar: IconSidebar,
    theme: IconTheme,
    themeMobile: IconThemeMobile,
    preferences: {
      active: IconPreferencesActive,
      inactiveMobile: IconPreferencesInactiveMobile,
      inactive: IconPreferencesInactive,
    },
    help: IconHelp,
    ichiWhite: IconIchiWhite,
    ichiMobile: IconIchiWhite,
    ichiOwned: IconIchiOwned,
    xIchiOwned: IconXIchiOwned,
    xIchiIchiRatio: IconXIchiIchiRatio,
    ichiApy: IconIchiApy,
    close: IconClose,
    newWindow: IconNewWindow,
    newWindowWhite: IconNewWindowWhite,
    lp: IconLp,
    eth: IconEth,
    ethWhite: IconEthWhite,
    maticWhite: IconMaticWhite,
    wbtc: IconWBTC,
    renFIL: IconRenFil,
    fuse: IconFuse,
    polygonBadge: BadgePolygon,
  },
  breakpoints: ['576px', '768px', '991px', '1220px'],
  space: [0, 5, 8, 10, 15, 20, 25, 30, 33, 35, 40, 50, 60, 70, 80, 85, 90, 100],
  fontSizes: [10, 12, 14, 15, 16, 18, 20, 22, 24, 36, 48, 80, 96],
  fontWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  lineHeights: {
    solid: 1,
    title: 1.25,
    copy: 1.5,
  },
  letterSpacings: {
    normal: 'normal',
    tracked: '0.1em',
    tight: '-0.05em',
    mega: '0.25em',
  },
  borders: [0, '1px solid', '2px solid', '3px solid', '4px solid', '5px solid', '6px solid'],
  radius: [3, 4, 5, 10, 20, 30, 60, 120, '50%'],
  widths: [36, 40, 44, 48, 54, 70, 81, 128, 256],
  heights: [36, 40, 44, 46, 48, 54, 70, 81, 128],
  maxWidths: [16, 32, 64, 128, 256, 512, 768, 1024, 1536],
  colors: darkColors,
  colorStyles: {
    primaryText: {
      color: darkColors.white as ColorsKey,
    },
    primary: {
      color: darkColors.primary as ColorsKey,
      borderColor: darkColors.transparent as ColorsKey,
      backgroundColor: darkColors.transparent as ColorsKey,
      '&:hover': {
        color: darkColors.primary as ColorsKey,
        backgroundColor: darkColors.transparent as ColorsKey,
      },
    },
    secondary: {
      color: darkColors.secondary as ColorsKey,
      borderColor: darkColors.transparent as ColorsKey,
      backgroundColor: darkColors.transparent as ColorsKey,
      '&:hover': {
        color: darkColors.secondaryHover as ColorsKey,
        borderColor: darkColors.transparent as ColorsKey,
        backgroundColor: darkColors.transparent as ColorsKey,
      },
    },
    warning: {
      color: darkColors.yellow as ColorsKey,
      borderColor: darkColors.yellow as ColorsKey,
      '&:hover': {
        color: darkColors.yellowHover as ColorsKey,
        borderColor: darkColors.yellowHover as ColorsKey,
      },
    },
    error: {
      color: darkColors.secondaryHover as ColorsKey,
      borderColor: darkColors.secondaryHover as ColorsKey,
      '&:hover': {
        color: darkColors.secondary as ColorsKey,
        borderColor: darkColors.secondary as ColorsKey,
      },
    },
    primaryWithBg: {
      color: darkColors.white as ColorsKey,
      backgroundColor: darkColors.primary as ColorsKey,
      borderColor: darkColors.primary as ColorsKey,
      border: '1px solid',
      '&:hover': {
        color: darkColors.white as ColorsKey,
        backgroundColor: darkColors.primaryHover as ColorsKey,
        borderColor: darkColors.primaryHover as ColorsKey,
        boxShadow: darkColors.primaryBoxShadow as ColorsKey,
      },
    },
    secondaryWithBg: {
      color: darkColors.black as ColorsKey,
      backgroundColor: darkColors.secondary as ColorsKey,
      borderColor: darkColors.secondary as ColorsKey,
      '&:hover': {
        color: darkColors.black as ColorsKey,
        backgroundColor: darkColors.secondaryHover as ColorsKey,
        borderColor: darkColors.secondaryHover as ColorsKey,
        boxShadow: darkColors.secondaryBoxShadow as ColorsKey,
      },
    },
    warningWithBg: {
      color: darkColors.white as ColorsKey,
      backgroundColor: darkColors.yellow as ColorsKey,
      borderColor: darkColors.yellow as ColorsKey,
      '&:hover': {
        backgroundColor: darkColors.yellowHover as ColorsKey,
        borderColor: darkColors.yellowHover as ColorsKey,
      },
    },
    errorWithBg: {
      color: darkColors.white as ColorsKey,
      backgroundColor: darkColors.secondaryHover as ColorsKey,
      borderColor: darkColors.secondaryHover as ColorsKey,
      '&:hover': {
        backgroundColor: darkColors.secondary as ColorsKey,
        borderColor: darkColors.secondary as ColorsKey,
      },
    },
    transparentBg: {
      backgroundColor: darkColors.white as ColorsKey,
      '&:hover': {
        backgroundColor: darkColors.white as ColorsKey,
      },
    },
  },
  buttonStyles: {
    primaryButton: {
      border: 'none',
      borderRadius: '20px',
      color: darkColors.primaryButtonColor as ColorsKey,
      backgroundColor: darkColors.primaryButtonBgColor as ColorsKey,
      padding: '10px',
      height: 'auto',
      '&:hover': {
        cursor: 'pointer',
        textDecoration: 'underline',
      },
    },
    textButton: {
      border: 0,
      color: darkColors.primary as ColorsKey,
      padding: 0,
      height: 'auto',
      backgroundColor: darkColors.transparent as ColorsKey,
    },
    outlined: {
      borderWidth: '1px',
      borderStyle: 'solid',
      backgroundColor: darkColors.transparent as ColorsKey,
    },
    fab: {
      border: '0',
      width: '40px',
      height: '40px',
      padding: 0,
      borderRadius: '50%',
      justifyContent: 'center',
      'span.btn-icon': {
        paddingLeft: 0,
      },
    },
    extendedFab: {
      border: '0',
      minWidth: '50px',
      height: '40px',
      borderRadius: '50px',
      justifyContent: 'center',
    },
  },
  // FlexBox: {
  //   backgroundColor: 'green'
  // }
};
